import React, { forwardRef } from 'react'

import { cn } from '@/utils/cn'

// Type-safe variants
const CARD_VARIANTS = {
  default: 'bg-white border border-gray-200 shadow-sm',
  outlined: 'bg-white border border-gray-300',
  elevated: 'bg-white shadow-lg border border-gray-100',
  ghost: 'bg-transparent'
} as const

const CARD_PADDING = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8'
} as const

const CARD_RADIUS = {
  none: 'rounded-none',
  sm: 'rounded',
  md: 'rounded-lg',
  lg: 'rounded-xl'
} as const

export type CardVariant = keyof typeof CARD_VARIANTS
export type CardPadding = keyof typeof CARD_PADDING
export type CardRadius = keyof typeof CARD_RADIUS

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant
  padding?: CardPadding
  radius?: CardRadius
  hover?: boolean
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      children,
      className,
      variant = 'default',
      padding = 'md',
      radius = 'md',
      hover = false,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          'transition-all duration-200',
          CARD_VARIANTS[variant],
          CARD_PADDING[padding],
          CARD_RADIUS[radius],
          hover && 'hover:shadow-md hover:scale-[1.02]',
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }
)

Card.displayName = 'Card'

// Compound components for better composition
export const CardHeader = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ children, className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col space-y-1.5 p-6 pb-4', className)} {...props}>
      {children}
    </div>
  )
)
CardHeader.displayName = 'CardHeader'

export const CardTitle = forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ children, className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn('text-lg font-semibold leading-none tracking-tight text-gray-900', className)}
      {...props}
    >
      {children}
    </h3>
  )
)
CardTitle.displayName = 'CardTitle'

export const CardDescription = forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ children, className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm text-gray-600', className)} {...props}>
    {children}
  </p>
))
CardDescription.displayName = 'CardDescription'

export const CardContent = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ children, className, ...props }, ref) => (
    <div ref={ref} className={cn('p-6 pt-0', className)} {...props}>
      {children}
    </div>
  )
)
CardContent.displayName = 'CardContent'

export const CardFooter = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ children, className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center p-6 pt-0', className)} {...props}>
      {children}
    </div>
  )
)
CardFooter.displayName = 'CardFooter'
